import React, { useEffect } from 'react';
import { Organization, ICertificate } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { Box, FormGroup, Typography } from '@material-ui/core';
import { PasslyButton } from '@app/components/Buttons';
import { Autorenew } from '@material-ui/icons';
import { localStore } from '@app/helpers/domStorage.helper';
import { useSelector } from 'react-redux';
import { defaultOrgId } from '@app/helpers/constants';
import { red } from '@material-ui/core/colors';
import { theme } from '@app/helpers';

interface IProps {
    connected: boolean;
    onDisconnect: () => void;
    domain?: string;
    username?: string;
}

export const ManualConfig = ({ connected, onDisconnect, domain, username }: IProps) => {
    const { translation } = useTypedTranslation();

    const organizations: Organization[] = useSelector((state: any) => state.basic.shellConfig.organizations);
    const homeRealmSuffix: string = useSelector((state: any) => state.basic.shellConfig.configs.homeRealmSuffix);
    const appCertificate: ICertificate = useSelector((state: any) => state.appLibrary.appCertificate);

    useEffect(() => {
        if (appCertificate === undefined || appCertificate.CerEncoded === undefined || appCertificate.CerEncoded === '') {
            return;
        }

        const spaces = `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`;

        let certSplit = appCertificate.RawData.match(/.{1,76}/g);

        let certEncoded = '';

        if (certSplit !== null) {
            certSplit.forEach(function (line, index) {
                if (index + 1 < certSplit!.length) {
                    certEncoded += '"' + line + '" +<br />' + spaces;
                } else {
                    certEncoded += '"' + line + '"<br />';
                }
            });
        }

        let certElement = document.querySelector('#cert');
        certElement!.innerHTML = certEncoded;
    }, [appCertificate]);

    const generateHomeRealm = () => {
        const orgId = localStore.getItem('org_filter') || defaultOrgId;

        let hr = '';

        organizations.forEach(function (o) {
            if (o.id === orgId) {
                hr = o.homeRealm;
            }
        });

        return `${hr}.${homeRealmSuffix}`;
    };

    return (
        <>
            {/* CONNECTED */}
            {connected && (
                <Box pb={1}>
                    <Typography variant='h3'>{translation('MSOL_DISCONNECT_HEADER')}</Typography>
                    <div>
                        <div className='danger'>
                            <b>{translation('MSOL_WARNING_LABEL')}</b>:&nbsp;
                            <span>
                                {translation('MSOL_DISCONNECT_WARNING')}
                            </span>
                        </div>
                        <PasslyButton
                            variant='contained'
                            style={{ backgroundColor: red[400], color: theme.palette.primary.contrastText }}
                            onClick={onDisconnect}
                            startIcon={<Autorenew />}
                        >
                            {translation('MSOL_DISCONNECT')}
                        </PasslyButton>
                    </div>
                </Box>
            )}

            <Typography variant='h3'>{translation('MSOL_CONFIGURE_MANUAL_HEADER')}</Typography>
            <FormGroup>
                <div>
                    <Typography variant='h3'>{translation('MSOL_PREREQUISITES')}</Typography>
                    <ul>
                        <li>
                            <a href='http://go.microsoft.com/fwlink/p/?LinkId=286152' target='_blank' rel='noopener noreferrer'>
                                {translation('MSOL_POWERSHELL_SSA_INSTALLER')}
                            </a>
                        </li>
                        <li>
                            <a
                                href='https://docs.microsoft.com/en-us/office365/enterprise/powershell/connect-to-office-365-powershell#connect-with-the-microsoft-azure-active-directory-module-for-windows-powershell'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {translation('MSOL_POWERSHELL_INSTALLER_X64')}
                            </a>
                        </li>
                    </ul>

                    <Typography variant='h3'>{translation('MSOL_MANUAL_POWERSHELL_HEADER')}</Typography>
                    <ol>
                        <li>
                            <span>{translation('MSOL_MANUAL_STEP1')}</span>

                            <blockquote>
                                <code>
                                    $creds = Get-Credential -Username <span>"{username}"</span> -Message "Configure Office 365
                                    Federation"
                                    <br />
                                    Connect-MSOLService -Credential $creds
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('MSOL_MANUAL_STEP2')}</span>
                            <blockquote>
                                <code>
                                    $domain = "<span>{domain}</span>"<br />
                                    $issuer = "https://<span>{domain}.{generateHomeRealm()}</span>/trust"
                                    <br />
                                    $passiveLogon = "https://<span>{generateHomeRealm()}</span>/trust/launch"
                                    <br />
                                    $activeLogon = "https://<span>{generateHomeRealm()}</span>/services/trust/2005/mixed"
                                    <br />
                                    $mexUri = "https://<span>{generateHomeRealm()}</span>/services/trust/mex"
                                    <br />
                                    $signingCert = <span id='cert' />
                                    <br />
                                    <br />
                                    Set-MsolDomainFederationSettings -DomainName $domain -IssuerUri $issuer -PassiveLogOnUri $passiveLogon
                                    -ActiveLogOnUri $activeLogon -MetadataExchangeUri $mexUri -SigningCertificate $signingCert
                                    <br />
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('MSOL_MANUAL_STEP3')}</span>

                            <blockquote>
                                <code>Get-MsolDomainFederationSettings -DomainName $domain</code>
                            </blockquote>
                        </li>
                    </ol>
                </div>
            </FormGroup>
        </>
    );
};
